import React, { useState } from "react";

export default function EnergyRingAvatar({
  image = "https://i.pravatar.cc/200?img=12",
  size = 104,
  name = "User",
  theme = "gold", // "gold" | "purple"
  isPro = false,
  className = "",
}) {
  const [active, setActive] = useState(false);
  const isPurpleTheme = theme === "purple" || isPro;

  // Dynamically scale ring metrics proportional to avatar size
  const ringGap = Math.max(3, Math.round(size * 0.1));
  const outerPad = Math.max(2, Math.round(size * 0.06));
  const ring = size + ringGap * 2;
  const outer = ring + outerPad * 2;
  const particle1Size = Math.max(2.5, Math.min(4.5, size * 0.04));
  const particle2Size = Math.max(2, Math.min(3.5, size * 0.03));
  const blurRadius = Math.max(6, Math.round(size * 0.12));

  return (
    <div className={`flex items-center justify-center shrink-0 ${className}`}>
      <style>{`
        @keyframes energy-spin-cw  { to { transform: rotate(360deg); } }
        @keyframes energy-spin-ccw { to { transform: rotate(-360deg); } }
        @keyframes energy-pulse {
          0%, 100% { opacity: 0.3;  transform: scale(1); }
          50%       { opacity: 0.7;  transform: scale(1.06); }
        }
        @keyframes energy-breathe {
          0%, 100% { transform: scale(1); }
          50%       { transform: scale(1.012); }
        }
        @keyframes energy-orbit-dot {
          0%, 100% { opacity: 0.15; }
          50%       { opacity: 1; }
        }
        @keyframes energy-shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @media (prefers-reduced-motion: reduce) {
          .energy-anim { animation: none !important; }
        }
      `}</style>

      <div
        role="button"
        tabIndex={0}
        aria-label={`${name}'s avatar with energy ring`}
        onMouseEnter={() => setActive(true)}
        onMouseLeave={() => setActive(false)}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
        className="relative inline-flex items-center justify-center rounded-xl outline-none
                   focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:ring-offset-[#07080B]"
        style={{
          width: outer,
          height: outer,
          animation: "energy-breathe 5s ease-in-out infinite",
          transition: "transform 0.3s ease",
        }}
      >
        {/* ── pulsing neon glow behind everything ── */}
        <div
          className="energy-anim absolute inset-0 rounded-xl pointer-events-none"
          style={{
            background: isPurpleTheme
              ? "radial-gradient(circle, rgba(168,85,247,0.7) 0%, rgba(139,92,246,0.35) 45%, transparent 72%)"
              : "radial-gradient(circle, rgba(251,146,60,0.65) 0%, rgba(245,158,11,0.28) 45%, transparent 72%)",
            filter: `blur(${blurRadius}px)`,
            animation: `energy-pulse ${active ? 1.8 : 3}s ease-in-out infinite`,
            willChange: "opacity, transform",
          }}
        />

        {/* ── ambient drop shadow ── */}
        <div
          className="absolute rounded-xl"
          style={{
            inset: 4,
            boxShadow: active
              ? (isPurpleTheme
                  ? "0 16px 36px rgba(0,0,0,0.65), 0 0 22px rgba(168,85,247,0.45)"
                  : "0 16px 36px rgba(0,0,0,0.65), 0 0 20px rgba(251,146,60,0.35)")
              : "0 8px 22px rgba(0,0,0,0.5)",
            transition: "box-shadow 0.4s ease-out",
          }}
        />

        {/* ── outer ring — clockwise, rich conic gradient ── */}
        <div
          className="energy-anim absolute rounded-xl"
          style={{
            inset: 0,
            padding: 2,
            background: isPurpleTheme
              ? "conic-gradient(from 0deg, #c084fc 0%, #a855f7 25%, #ffffff 48%, #9333ea 72%, #e879f9 100%)"
              : "conic-gradient(from 0deg, #FACC15 0%, #F97316 25%, #ffffff 48%, #F59E0B 72%, #FACC15 100%)",
            WebkitMask:
              "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            animation: `energy-spin-cw ${active ? 5 : 8}s linear infinite`,
            willChange: "transform",
          }}
        />

        {/* ── inner dashed ring — counter-clockwise ── */}
        <svg
          className="energy-anim absolute"
          style={{
            inset: 6,
            width: "calc(100% - 12px)",
            height: "calc(100% - 12px)",
            animation: `energy-spin-ccw ${active ? 7 : 11}s linear infinite`,
          }}
          viewBox="0 0 100 100"
        >
          <rect
            x="4" y="4" width="92" height="92" rx="10"
            fill="none"
            stroke={`url(#${isPurpleTheme ? 'dash-grad-purple' : 'dash-grad'})`}
            strokeWidth="1.4"
            strokeDasharray="8 6"
            strokeLinecap="round"
            opacity="0.9"
          />
          <defs>
            <linearGradient id="dash-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor="#FDE68A" />
              <stop offset="50%"  stopColor="#FDBA74" />
              <stop offset="100%" stopColor="#FDE68A" />
            </linearGradient>
            <linearGradient id="dash-grad-purple" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor="#f5d0fe" />
              <stop offset="50%"  stopColor="#c084fc" />
              <stop offset="100%" stopColor="#f5d0fe" />
            </linearGradient>
          </defs>
        </svg>

        {/* ── glass blur layer between rings and image ── */}
        <div
          className="absolute rounded-xl pointer-events-none"
          style={{
            inset: 10,
            background: isPurpleTheme ? "rgba(168,85,247,0.06)" : "rgba(255,255,255,0.04)",
            backdropFilter: "blur(4px)",
            border: isPurpleTheme ? "1px solid rgba(192,132,252,0.25)" : "1px solid rgba(255,255,255,0.1)",
          }}
        />

        {/* ── orbiting particle 1 ── */}
        <div
          className="energy-anim absolute inset-0"
          style={{ animation: `energy-spin-cw ${active ? 3.5 : 6}s linear infinite` }}
          aria-hidden="true"
        >
          <span
            className="energy-anim absolute rounded-full"
            style={{
              width: particle1Size, height: particle1Size,
              left: "50%", top: 1,
              marginLeft: -particle1Size / 2,
              background: "#FFFDF0",
              boxShadow: isPurpleTheme ? "0 0 8px 2px rgba(240,171,252,0.95)" : "0 0 7px 2px rgba(255,255,255,0.9)",
              animation: "energy-orbit-dot 2.2s ease-in-out infinite",
            }}
          />
        </div>

        {/* ── orbiting particle 2 ── */}
        <div
          className="energy-anim absolute inset-0"
          style={{ animation: `energy-spin-ccw ${active ? 4.5 : 7.5}s linear infinite` }}
          aria-hidden="true"
        >
          <span
            className="energy-anim absolute rounded-full"
            style={{
              width: particle2Size, height: particle2Size,
              left: "50%", bottom: 4,
              marginLeft: -particle2Size / 2,
              background: isPurpleTheme ? "#e879f9" : "#FDBA74",
              boxShadow: isPurpleTheme ? "0 0 6px 2px rgba(168,85,247,0.9)" : "0 0 6px 2px rgba(251,146,60,0.85)",
              animation: "energy-orbit-dot 3s ease-in-out infinite",
              animationDelay: "0.9s",
            }}
          />
        </div>

        {/* ── avatar image ── */}
        <div
          className="relative overflow-hidden rounded-xl bg-gray-800"
          style={{ width: size, height: size }}
        >
          <img
            src={image}
            alt=""
            className="h-full w-full object-cover"
            draggable={false}
          />
          {/* top-edge gloss */}
          <div
            className="pointer-events-none absolute inset-0 rounded-xl"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 38%)",
            }}
          />
          {/* shimmer sweep on hover */}
          {active && (
            <div
              className="pointer-events-none absolute inset-0 rounded-xl"
              style={{
                background:
                  "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.18) 50%, transparent 60%)",
                backgroundSize: "200% 100%",
                animation: "energy-shimmer 1.2s ease-in-out infinite",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
